import React, { useRef, useMemo, useState, useEffect, useCallback } from "react";
import { Alert, Button, Icon } from "rsuite";
import axios from "axios";
import styled, { createGlobalStyle } from "styled-components";

import { checkPermissionsFor } from "store/storeHelpers";
import { useWindowWidth } from "hooks";
import { weekDaysData, DEFAULT_PER_PAGE_MEDIUM } from "const";
import { resolvePerPage } from "utils/helpers";
import {
    FAKE_DLR_CREATE_API,
    FAKE_DLR_MODIFY_API,
    FAKE_DLR_REMOVE_API
} from "const/apiMethods";
import { LS_FAKE_DLR_PER_PAGE } from "const/localStorageKeys";
import { FilterSectionFlexboxGrid, StyledItemFitWidth, StyledItemAutoMargin } from "components/base/FitContainers";
import PanelLayout from "components/base/PanelLayout";
import PageHeader from "components/base/PageHeader";
import { Spacer } from "components/base/Spacer";
import { ButtonPrimary } from "components/base/BaseButton";
import FakeDLRFilters from "./FakeDLRFilters";
import FakeDLREditModal from "./FakeDLREditModal";
import FakeDLRTable from "./FakeDLRTable";

const normalizeTimeValue = (timeValue) => {
    if (timeValue instanceof Date && !Number.isNaN(timeValue.getTime())) {
        const pad = (value) => String(value).padStart(2, "0");
        return `${pad(timeValue.getHours())}:${pad(timeValue.getMinutes())}:00`;
    }

    if (typeof timeValue === "string") {
        const [hours = "00", minutes = "00"] = timeValue.split(":");
        return `${hours}:${minutes}:00`;
    }

    return "";
};

const normalizeWeekDaysValue = (weekDays) => {
    if (!Array.isArray(weekDays)) {
        return [];
    }

    if (weekDays.every(day => typeof day === "number")) {
        return weekDays;
    }

    return weekDays
        .filter(day => day && day.checked)
        .map(day => day.id);
};

const mapWeekDaysToBackend = (weekDayIds = []) => {
    return weekDaysData.map(day => {
        return {
            id: day.value,
            name: day.label,
            checked: weekDayIds.includes(day.value)
        };
    });
};

const normalizeFakeDLRPayload = (payload = {}) => ({
    ...(() => {
        const { time_range, ...restPayload } = payload;
        const timeRange = Array.isArray(time_range)
            ? time_range
            : (Array.isArray(payload.from_time) ? payload.from_time : null);

        const fromTime = timeRange ? timeRange[0] : payload.from_time;
        const toTime = timeRange ? timeRange[1] : payload.to_time;

        return {
            ...restPayload,
            from_time: normalizeTimeValue(fromTime),
            to_time: normalizeTimeValue(toTime)
        };
    })(),
    week_days: normalizeWeekDaysValue(payload.week_days)
});


const FakeDLRPage = ({
    data,
    page,
    count,
    perPage,
    loading,
    service,

    e212List,
    countryList,
    supplierList,
    operatorDropdownList,
    operatorDropdownLoading,
    countryDropdownList,
    countryDropdownLoading,
    referencesLoading,

    createApi,
    modifyApi,
    testRuleApi,
    modifyMultirowApi,
    removeApi,

    getList,
    getSupplierDropdownList,
    getE212DropdownList,
    getOperatorDropdownList,
    getCountryDropdownList,
    getFakeDLRSupplierList,
    getReferencesForReports
}) => {
    const cancelToken = useRef(axios.CancelToken.source());

    const windowWidth = useWindowWidth();
    const isMobile = windowWidth < 768;

    const defaultFilter = {};

    const [filter, setFilter] = useState(defaultFilter);

    // Стейт для редагування таблиці
    const [editData, setEditData] = useState({
        original: [], // Оригінальні дані
        draft: [],    // Робоча копія для редагування
        mode: "single" // "single" або "multi"
    });

    const [removeKeys, setRemoveKeys] = useState([]);
    const [resetToken, setResetToken] = React.useState(0);
    const [modalState, setModalState] = useState({ mode: null, rowData: null });
    const isDataInitialized = useRef(false);

    const defaultSort = { column: "supplier_id", type: "asc" };

    const [pageState, setPageState] = useState(page);
    const [perPageState, setPerpageState] = useState(() => resolvePerPage(
        LS_FAKE_DLR_PER_PAGE,
        perPage,
        DEFAULT_PER_PAGE_MEDIUM
    ));
    const [sort, setSort] = useState(defaultSort);

    const getSupplierPerTrunkList = useCallback((disableInactive = false) => {
        return supplierList.reduce((result, item) => {
            const trunkList = (item.trunk_list || [])
                .filter(trunk => trunk.type !== "gan")
                .map(trunk => ({
                    id: `${item.supplier_id}%${trunk.id}`,
                    trunk_id: disableInactive && trunk.active === false ? null : trunk.id,
                    trunk_name: trunk.name,
                    trunk_type: trunk.type,
                    active: trunk.active,
                    supplier_id: item.supplier_id,
                    supplier_name: item.name,
                    supplier: item.name,
                    account_manager_name: item.account_manager_name,
                    is_viewed: item.is_viewed
                }));

            result.push(...trunkList);

            // "No trunks" потрібен лише для звичайного списку.
            // У create-списку неактивні транки присутні, але disabled.
            if (!disableInactive && !item.trunk_list?.length) {
                result.push({
                    id: item.supplier_id,
                    trunk_id: null,
                    trunk_name: "No trunks",
                    trunk_type: null,
                    supplier_id: item.supplier_id,
                    supplier_name: item.name,
                    supplier: item.name,
                    is_viewed: item.is_viewed
                });
            }

            return result;
        }, []);
    }, [supplierList])

    useEffect(() => {
        getE212DropdownList({}, true);
        getOperatorDropdownList({});
        getCountryDropdownList({});
        getReferencesForReports();
        getSupplierDropdownList();
        getFakeDLRSupplierList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        onDataUpdate(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, sort]);

    // Ініціалізація даних при завантаженні
    useEffect(() => {
        if (data && data.length > 0 && !isDataInitialized.current) {
            const preparedData = data.map(item => ({
                ...item,
                edited: false // Поле для відстеження змін
            }));

            setEditData({
                original: preparedData,
                draft: JSON.parse(JSON.stringify(preparedData)), // Глибока копія
                mode: "single"
            });

            isDataInitialized.current = true;
        }
    }, [data]);

    const onDataUpdate = (_page, _perPage) => {
        if (typeof cancelToken.current !== typeof undefined) {
            cancelToken.current.cancel("Operation canceled due to new request.");
        }

        cancelToken.current = axios.CancelToken.source();
        filter.cancelToken = cancelToken.current;

        isDataInitialized.current = false;
        const currentPage = _page ?? pageState;
        const currentPerPage = _perPage ?? perPageState;
        getList(filter, currentPage, currentPerPage, sort);
    };

    const getItems = (nextPage = 1, nextPerPage = perPageState) => {
        setPageState(nextPage);
        setPerpageState(nextPerPage);
        onDataUpdate(nextPage, nextPerPage);
    };

    const handleCreate = async (params) => {
        const { id: supplierAndTrunkId, ...restParams } = params;
        const [supplierId, trunkId] = supplierAndTrunkId.split("%");
        const normalizedParams = normalizeFakeDLRPayload(restParams);

        const payload = {
            ...normalizedParams,
            from_time: normalizeTimeValue(normalizedParams.from_time),
            to_time: normalizeTimeValue(normalizedParams.to_time),
            percent: parseFloat(normalizedParams.percent / 100),
            min_calls_per_hour: parseInt(normalizedParams.min_calls_per_hour, 10) || 0,
            week_days: mapWeekDaysToBackend(normalizedParams.week_days)
        };

        const r = await createApi({
            target: {
                "sms.supplier_id": supplierId,
                "sms.supplier_trunk_id": trunkId
            },
            ...payload
        });

        if (r?.result?.toLowerCase() === "ok") {
            Alert.success(`Fake dlr was added`);
        }

        return r;
    };

    const handleModify = async (params, defaultData) => {
        const { "sms.fake_dlr_id": id, id: supplierAndTrunkId, ...restParams } = params;
        const [supplierId, trunkId] = supplierAndTrunkId.split("%");
        const normalizedParams = normalizeFakeDLRPayload(restParams);
        const payload = {
            ...normalizedParams,
            from_time: normalizeTimeValue(normalizedParams.from_time),
            to_time: normalizeTimeValue(normalizedParams.to_time),
            percent: parseFloat(normalizedParams.percent / 100),
            min_calls_per_hour: parseInt(normalizedParams.min_calls_per_hour, 10) || 0,
            week_days: mapWeekDaysToBackend(normalizedParams.week_days)
        };

        const r = await modifyApi({
            "sms.fake_dlr_id": id,
            "sms.supplier_id": supplierId,
            "sms.supplier_trunk_id": trunkId
        }, payload);

        if (r?.result?.toLowerCase() === "ok") {
            Alert.success(`Fake dlr was modified`);
        }

        return r;
    };

    const handleRemove = async (id) => {
        const r = await removeApi(id);

        if (r?.result?.toLowerCase() === "ok") {
            Alert.success(`Fake dlr was removed`);
        }

        return r;
    };

    const handleMultirowModify = async () => {
        const targetParams = {};
        const params = {};

        const draftEditedData = editData.draft ? editData.draft.filter(i => i.edited) : [];
        const removeSet = new Set(removeKeys);

        const modifiedMap = new Map(
            draftEditedData
                .filter(({ "sms.fake_dlr_id": id }) => !removeSet.has(id))
                .map(item => [item["sms.fake_dlr_id"], item])
        );

        // Об’єднаний список ключів без дублів:
        const allIds = Array.from(new Set([
            ...removeSet,
            ...modifiedMap.keys(),
        ]));

        const dataList = allIds.map(id => {
            if (removeSet.has(id)) {
                return { "sms.fake_dlr_id": id, action: "remove" };
            }

            const item = modifiedMap.get(id);
            const normalizedItem = normalizeFakeDLRPayload(item);
            const payload = {
                ...normalizedItem,
                "sms.fake_dlr_id": id,
                action: "modify",
                percent: parseFloat(normalizedItem.percent / 100),
            };

            return payload;
        });

        const r = await modifyMultirowApi(targetParams, params, dataList)

        if (r?.result?.toLowerCase() === "ok") {
            onDataUpdate();
            Alert.success(`Changes were saved`);
        }

        return r;
    };

    const renderSuppliersItem = (label, { supplier_name, trunk_name }) => {
        return (
            <SupplierGroupItem>
                {supplier_name} / {trunk_name}
            </SupplierGroupItem>
        );
    };

    const renderSuppliersValue = (value, item) => {
        if (!item) return "";

        return `${item.supplier_name} / ${item.trunk_name}`;
    };

    const convertedData = useMemo(() => {
        return data.map((value, idx) => {
            if (idx === 0) {
                value.first = true;
            }

            value.id = `${value["sms.supplier_id"]}%${value["sms.supplier_trunk_id"]}`;
            value.percent = value.percent !== null && value.percent !== undefined ? Number((value.percent * 100).toFixed(2)) : value.percent;
            value.mccMnc = `${value.mcc}${value.mnc ? "-" + value.mnc : ""}`;
            value.mccCountryName = value.mcc ? `${value.country_name} (${value.mcc})` : value.country_name;

            return value;
        });
    }, [data]);

    const loadingWithReferences = loading || referencesLoading || countryDropdownLoading || operatorDropdownLoading
    const createPermission = checkPermissionsFor(FAKE_DLR_CREATE_API);
    const supplierPerTrunkList = getSupplierPerTrunkList();
    const createSupplierList = getSupplierPerTrunkList(true);

    return (
        <PanelLayout>
            <GlobalStyle />
            <PageHeader title="Fake DLR" />
            <FilterSectionFlexboxGrid controlable={!isMobile} centered>
                <StyledItemFitWidth isMobile={isMobile} controlable={!isMobile}>
                    <FakeDLRFilters
                        loading={loadingWithReferences}
                        disabled={loadingWithReferences}
                        editData={editData}
                        isMobile={isMobile}
                        operatorList={operatorDropdownList}
                        countryList={countryDropdownList}
                        defaultFilter={defaultFilter}

                        renderSuppliersItem={renderSuppliersItem}

                        setFilter={setFilter}
                        setEditData={setEditData}
                        setResetToken={setResetToken}
                        setRemoveKeys={setRemoveKeys}
                        setPageState={setPageState}
                        getSupplierPerTrunkList={getSupplierPerTrunkList}
                    />
                </StyledItemFitWidth>
                <StyledItemAutoMargin isMobile={isMobile} style={{ alignSelf: "flex-start" }}>
                    {editData.mode === "multiple" ? <MultipleEditRowPanel
                        editData={editData}
                        removeKeys={removeKeys}
                        isMobile={isMobile}
                        setEditData={setEditData}
                        setRemoveKeys={setRemoveKeys}
                        // handleModify={handleModifyMultipleLimits}
                        // handleRemove={handleRemoveMultipleLimits}
                        handleChanges={handleMultirowModify}
                    /> : <ButtonPrimary
                        disabled={loadingWithReferences || !createPermission}
                        onClick={() => setModalState({ mode: "create", rowData: null })}
                    >
                        <Icon icon="plus" /> Add new
                    </ButtonPrimary>}
                </StyledItemAutoMargin>
            </FilterSectionFlexboxGrid>
            {!isMobile && <Spacer />}
            <FakeDLRTable
                data={convertedData}
                activePage={pageState}
                count={count}
                perPage={perPageState}

                service={service}
                loading={loadingWithReferences}
                editData={editData}
                removeKeys={removeKeys}
                isMobile={isMobile}
                operatorList={operatorDropdownList}
                countryList={countryDropdownList}

                renderSuppliersItem={renderSuppliersItem}
                renderSuppliersValue={renderSuppliersValue}
                perPageLSKey={LS_FAKE_DLR_PER_PAGE}
                getItems={getItems}

                setEditData={setEditData}
                setRemoveKeys={setRemoveKeys}

                onDataUpdate={onDataUpdate}
                onRowCreate={handleCreate}
                onRowSubmit={handleModify}
                onRowEdit={(rowData) => setModalState({ mode: "edit", rowData })}
                onRowRemove={handleRemove}
                modifyPermission={checkPermissionsFor(FAKE_DLR_MODIFY_API)}
                removePermission={checkPermissionsFor(FAKE_DLR_REMOVE_API)}

                resetEditableRowsToken={resetToken}
                getSupplierPerTrunkList={getSupplierPerTrunkList}

            />

            <FakeDLREditModal
                show={!!modalState.mode}
                mode={modalState.mode}
                rowData={modalState.rowData}
                loading={loadingWithReferences}
                supplierList={modalState.mode === "create" ? createSupplierList : supplierPerTrunkList}
                countryList={countryDropdownList || []}
                operatorList={operatorDropdownList || []}
                renderSuppliersItem={renderSuppliersItem}
                renderSuppliersValue={renderSuppliersValue}
                onSubmit={modalState.mode === "create" ? handleCreate : handleModify}
                onTestRule={testRuleApi}
                onClose={(updated) => {
                    setModalState({ mode: null, rowData: null });
                    if (updated) {
                        onDataUpdate();
                    }
                }}
            />
        </PanelLayout>
    );
};

export default FakeDLRPage;


const MultipleEditRowPanel = ({ editData, removeKeys, setEditData, setRemoveKeys, handleChanges, ...props }) => {
    const hasModifyData = editData && editData.draft ? editData.draft.some((value) => value.edited) : false;
    const hasRemoveData = removeKeys && removeKeys.length;

    return (<FilterSectionFlexboxGrid controlable={!props.isMobile} style={{ marginTop: "20px" }}>
        <StyledItemFitWidth></StyledItemFitWidth>
        <StyledItemAutoMargin>
            <StyledButton
                color="green"
                appearance="primary"
                disabled={!hasModifyData && !hasRemoveData}
                onClick={async () => {
                    setRemoveKeys([]);
                    await handleChanges();
                }}
            >
                Save
                {hasModifyData ? <Icon icon="edit2" /> : <></>}
                {hasRemoveData ? <Icon icon="trash" /> : <></>}
            </StyledButton>
            <StyledButton
                color="red"
                appearance="primary"
                onClick={() => {
                    setEditData((prev) => {
                        return {
                            ...prev,
                            draft: prev.original,
                            mode: "single"
                        }
                    });
                    setRemoveKeys([]);
                }}
            >
                Cancel
            </StyledButton>
        </StyledItemAutoMargin>
    </FilterSectionFlexboxGrid>);
};

const StyledButton = styled(Button)`
    &&.rs-btn.rs-btn-primary {
        width: 140px;
        background-color: ${props => props.color === "green" ? "var(--color-success)" : "var(--color-error2)"} !important;
    }
     &&.rs-btn.rs-btn-primary > .rs-icon {
        margin-left: 10px;
     }
`;
const GlobalStyle = createGlobalStyle`
    .rs-picker-select-menu .rs-picker-select-menu-items.grouped .rs-picker-select-menu-group .rs-picker-select-menu-group-title .rs-picker-select-menu-group-caret {
        display: none;
        pointer-events: none;
    }

    .rs-picker-select-menu-items .rs-picker-select-menu-group-title {
        padding: 0 !important;
    }

    .rs-picker-select-menu-items .rs-picker-select-menu-group:not(:first-child) {
        margin-top: 0 !important;
        padding-top: 0 !important;
        border-top: none !important;
    }
`;

const SupplierGroup = styled.div`
    && {
        width: 100%;
        display: block;
        padding: 8px 12px;
        cursor: default;
        border-bottom: 1px solid #e5e5e5;
        border-top: 1px solid #e5e5e5;
        background: #f1f1f1;
    }
`;

const SupplierGroupItem = styled.div`
 
`;
